import EnumBadge from "../../components/ui/EnumBadge";
import CompactReference from "../../components/ui/CompactReference";
import Pagination from "../../components/ui/Pagination";
import React, { useState, useEffect } from "react";
import "./QuoteRequestPage.css";
import { Quote } from "../../types/quote";
import { FiMail, FiDownload } from "react-icons/fi";
import {
  PageFilterDropdown,
  PageSearchBar,
  PageToolbar,
} from "../../compoonents/PageToolbar";
import PageLoader from "../../compoonents/PageLoader";
import { emailQuote, downloadQuotePdf, getQuotes } from "../../services/quoteService";
import { getApiErrorMessage, retryAfterSeconds } from "../../utils/apiError";
import { saveDownload } from "../../utils/download";

// --- ICON IMPORTS ---
import emptyQuoteIcon from "../../assets/images/quote-icon.png";

const QuoteRequestPage: React.FC = () => {
  // Data State
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [filterService, setFilterService] = useState(""); // e.g., 'import', 'export', 'procurement'
  const [action, setAction] = useState<{ id: string; type: "email" | "download" } | null>(null);
  const [actionError, setActionError] = useState("");
  const [message, setMessage] = useState("");
  const [retryAt, setRetryAt] = useState<Record<string, number>>({});
  const [now, setNow] = useState(Date.now());

  const QUOTE_FILTER_OPTIONS = [
    { value: "import", label: "Import" },
    { value: "export", label: "Export" },
    { value: "procurement", label: "Procurement" },
  ];
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 2. Fetch Data (Debounced)
  useEffect(() => {
    const fetchQuotes = async () => {
      setLoading(true);
      setError(null);
      try {
        // Pass params to backend
        const response = await getQuotes(currentPage, searchQuery);
        setQuotes(response.items);
        setTotalPages(response.totalPages);
        setTotalItems(response.total);
      } catch (err) {
        setError("Failed to load quote requests.");
      } finally {
        setLoading(false);
      }
    };

    // Debounce to prevent flashing on every keystroke
    const timer = setTimeout(() => {
      fetchQuotes();
    }, 500);

    return () => clearTimeout(timer);
  }, [currentPage, searchQuery]);

  // 3. Client-Side Filtering (Fallback if backend doesn't filter)
  const visibleQuotes = quotes.filter((quote) => {
    // Search Filter (ID or Email)
    const lowerSearch = searchQuery.toLowerCase();
    const matchesSearch =
      quote.id.toLowerCase().includes(lowerSearch) ||
      quote.email.toLowerCase().includes(lowerSearch);

    // Dropdown Service Type Filter
    let matchesType = true;
    if (filterService) {
      // Check if the quote service type includes the filter (e.g. "Import" matches "Sea Import")
      matchesType = (quote.serviceType || "")
        .toLowerCase()
        .includes(filterService.toLowerCase());
    }

    return matchesSearch && matchesType;
  });

  // Handlers

  const handleFilterChange = (type: string) => {
    setFilterService(type);
    setCurrentPage(1);
  };

  const handleDownload = async (quote: Quote) => {
    if (action) return;
    setAction({ id: quote.id, type: "download" });
    setActionError("");
    try {
      const result = await downloadQuotePdf(quote.id);
      saveDownload(result.blob, result.filename);
      setMessage("Quote PDF downloaded.");
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Could not download the quote PDF."));
    } finally {
      setAction(null);
    }
  };

  const handleEmail = async (quote: Quote) => {
    const cooldown = Math.max(0, Math.ceil(((retryAt[quote.id] || 0) - now) / 1000));
    if (action || cooldown > 0) return;
    setAction({ id: quote.id, type: "email" });
    setActionError("");
    try {
      await emailQuote(quote.id);
      setMessage(`Quote email queued for ${quote.email}.`);
    } catch (err) {
      const seconds = retryAfterSeconds(err);
      if (seconds) setRetryAt(previous => ({ ...previous, [quote.id]: Date.now() + seconds * 1000 }));
      setActionError(getApiErrorMessage(err, "Could not queue the quote email."));
    } finally {
      setAction(null);
    }
  };

  return (
    <div className="quote-request-page">
      {actionError && <div className="page-error" role="alert">{actionError}</div>}
      {message && <div className="integration-success" role="status">{message}</div>}
      {/* HEADER - Always Visible */}
      <header className="page-header">
        <h3>All Quote Requests</h3>
        <div className="page-controls">
          <PageToolbar>
            <PageFilterDropdown
              value={filterService}
              options={QUOTE_FILTER_OPTIONS}
              onChange={handleFilterChange}
              allLabel="All Services"
            />
            <PageSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by Request ID, email..."
            />
          </PageToolbar>
        </div>
      </header>

      {/* ERROR MESSAGE */}
      {error && <div className="page-error">{error}</div>}

      {/* LOADING OVERLAY */}
      {loading && !error && <PageLoader message="Loading quote requests..." />}

      {/* CONTENT */}
      {!loading && !error && (
        <>
          {visibleQuotes.length === 0 ? (
            <div className="quote-request-page page--empty">
              {/* Check if empty due to search or actually empty */}
              {searchQuery || filterService ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <h3>No quotes found matching "{searchQuery}"</h3>
                </div>
              ) : (
                <>
                  <img src={emptyQuoteIcon} alt="No quote requests" />
                  <h3>No quote requests have been made yet</h3>
                </>
              )}
            </div>
          ) : (
            <div className="table-container admin-table-container">
              <table className="data-table admin-data-table">
                <thead>
                  <tr>
                    <th>Request ID</th>
                    <th>Email</th>
                    <th>Service</th>
                    <th>Location From</th>
                    <th>Location To</th>
                    <th>Goods Type</th>
                    <th>Weight</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleQuotes.map((quote) => (
                    <tr key={quote.id}>
                      <td data-label="Request ID">
                        <CompactReference value={quote.id} />
                      </td>
                      <td data-label="Email">{quote.email}</td>
                      <td data-label="Service"><EnumBadge value={quote.serviceType} /></td>
                      <td data-label="Location From">{quote.locationFrom}</td>
                      <td data-label="Location To">{quote.locationTo}</td>
                      <td data-label="Goods Type"><EnumBadge value={quote.goodsType} /></td>
                      <td data-label="Weight">{`${quote.weight}kg`}</td>
                      <td data-label="Date">
                        {new Date(quote.createdAt).toLocaleDateString()}
                      </td>
                      <td data-label="Action">
                        <div className="table-action-buttons">
                          <button
                              type="button"
                              className="table-action-btn"
                              aria-label={`Email quote to ${quote.email}`}
                              title="Email quote"
                              disabled={action?.id === quote.id || Math.max(0, Math.ceil(((retryAt[quote.id] || 0) - now) / 1000)) > 0}
                              onClick={() => void handleEmail(quote)}
                            >
                              <FiMail />
                          </button>
                          <button
                              type="button"
                              className="table-action-btn"
                              aria-label={`Download quote ${quote.id}`}
                              title="Download quote PDF"
                              disabled={action?.id === quote.id}
                              onClick={() => void handleDownload(quote)}
                            >
                              <FiDownload />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* PAGINATION */}
          {visibleQuotes.length > 0 && (
            <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} onPageChange={setCurrentPage} loading={loading} />
          )}
        </>
      )}
    </div>
  );
};

export default QuoteRequestPage;
